import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import { TbFilterCog } from "react-icons/tb";
import PortalDropdown from "./PortalDropdown";
import "../../assets/css/checklist.css";

function getIdeaTags(idea) {
  return Array.isArray(idea?.tags) ? idea.tags.filter(Boolean) : [];
}

function getIdeaAttachments(idea) {
  return Array.isArray(idea?.attachment_urls)
    ? idea.attachment_urls.filter(Boolean)
    : [];
}

function stripHtml(value) {
  if (!value) return "";

  if (typeof window !== "undefined") {
    const parsed = new window.DOMParser().parseFromString(value, "text/html");
    return parsed.body.textContent || "";
  }

  return value.replace(/<[^>]*>/g, " ");
}

function getIdeaPreviewText(idea) {
  const text = stripHtml(idea?.description || "").replace(/\s+/g, " ").trim();
  if (!text) return "No description added yet.";
  if (text.length <= 180) return text;
  return `${text.slice(0, 177).trim()}...`;
}

export default function IdeasExpandMorePopup({
  show,
  handleClose,
  ideas,
  loading,
  error,
  searchTerm,
  selectedTags,
  availableTagOptions,
  hasActiveFilters,
  onSearchTermChange,
  onSelectedTagsChange,
  onClearFilters,
  onCreateIdea,
  onEditIdea,
  onDeleteIdea,
  onBulkDelete,
  bulkDeleteDisabled = false,
  isBulkDeleting = false,
}) {
  const [showModalFilters, setShowModalFilters] = useState(false);

  useEffect(() => {
    if (show) {
      setShowModalFilters(hasActiveFilters);
    }
  }, [show, hasActiveFilters]);

  const hasIdeas = ideas.length > 0;

  const summaryText = useMemo(() => {
    if (loading) return "Loading ideas...";
    if (error) return error;
    if (!hasIdeas) return hasActiveFilters ? "No ideas match the current filters." : "No ideas saved yet.";
    return `${ideas.length} ideas`;
  }, [error, hasActiveFilters, hasIdeas, ideas.length, loading]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      className="task-popup ideas-popup"
    >
      <Modal.Header closeButton>
        <Modal.Title>Ideas</Modal.Title>
      </Modal.Header>

      <Modal.Body className="ideas-popup__body">
        <div className="ideas-popup__panel">
          <div className="checklist-tabs-header ideas-popup__toolbar">
            <div>
              <div className="ideas-popup__title">Ideas</div>
              <div className="ideas-popup__summary">{summaryText}</div>
            </div>

            <div className="ideas-popup__actions">
              <button
                type="button"
                className="ideas-sidebar__header-add"
                onClick={onCreateIdea}
                title="Add ideas"
              >
                <i className="bi bi-plus"></i>
              </button>
              <button
                type="button"
                className="ideas-popup__bulk-delete"
                onClick={onBulkDelete}
                title="Delete all ideas"
                disabled={bulkDeleteDisabled || isBulkDeleting}
              >
                {isBulkDeleting ? <Spinner animation="border" size="sm" /> : <i className="bi bi-trash"></i>}
              </button>
              <button
                type="button"
                className={`filter-toggle-btn ${showModalFilters || hasActiveFilters ? "active" : ""}`}
                onClick={() => setShowModalFilters((current) => !current)}
                title={showModalFilters || hasActiveFilters ? "Hide filters" : "Show filters"}
              >
                <TbFilterCog size={18} />
              </button>
            </div>
          </div>

          {(showModalFilters || hasActiveFilters) ? (
            <div className="ideas-popup__filters">
              <div className="d-flex gap-2">
                <Select
                  isMulti
                  isSearchable
                  closeMenuOnSelect={false}
                  hideSelectedOptions={false}
                  placeholder="Select tags"
                  className="popup-select-container flex-fill"
                  classNamePrefix="popup-select"
                  options={availableTagOptions}
                  value={selectedTags}
                  onChange={(options) => onSelectedTagsChange(Array.isArray(options) ? options : [])}
                />

                {hasActiveFilters ? (
                  <button
                    type="button"
                    className="ideas-sidebar__clear-btn"
                    onClick={onClearFilters}
                    title="Clear filters"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                ) : null}
              </div>

              <Form.Control
                type="text"
                className="search-input-box"
                placeholder="Search ideas..."
                value={searchTerm}
                onChange={(event) => onSearchTermChange(event.target.value)}
              />
            </div>
          ) : null}

          <div className="ideas-popup__content modal-scroll">
            {loading ? (
              <div className="ideas-popup__feedback">
                <Spinner animation="border" size="sm" />
                <span>Loading ideas...</span>
              </div>
            ) : null}

            {!loading && error ? <div className="ideas-popup__feedback">{error}</div> : null}

            {!loading && !error && !hasIdeas ? (
              <div className="ideas-popup__feedback">{summaryText}</div>
            ) : null}

            {!loading &&
              !error &&
              ideas.map((idea) => {
                const tags = getIdeaTags(idea);
                const attachments = getIdeaAttachments(idea);

                return (
                  <article
                    key={idea.id}
                    className="ideas-popup__card"
                  >
                    <div className="ideas-popup__card-shell">
                      <div
                        className="ideas-popup__card-clickable"
                        onClick={() => onEditIdea(idea)}
                      >
                        <div className="ideas-popup__card-header">
                          <div className="ideas-popup__icon">
                            <i className="bi bi-lightbulb"></i>
                          </div>

                          <div className="ideas-popup__card-main">
                            <h4 className="ideas-popup__card-title">{idea.title || "Untitled ideas"}</h4>
                            <p className="ideas-popup__card-description">{getIdeaPreviewText(idea)}</p>
                          </div>
                        </div>

                        {tags.length > 0 ? (
                          <div className="ideas-popup__tags">
                            {tags.map((tag) => (
                              <span className="ideas-popup__tag" key={`${idea.id}-${tag}`}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        ) : null}

                        {attachments.length > 0 ? (
                          <div className="ideas-popup__meta">
                            <span>{attachments.length} attachment{attachments.length === 1 ? "" : "s"}</span>
                          </div>
                        ) : null}
                      </div>

                      <div className="ideas-popup__card-actions">
                        <PortalDropdown
                          align="end"
                          menuItems={[
                            {
                              icon: "bi bi-pencil",
                              label: "Edit",
                              onClick: () => onEditIdea(idea),
                            },
                            {
                              icon: "bi bi-trash",
                              label: "Delete",
                              danger: true,
                              onClick: () => onDeleteIdea(idea),
                            },
                          ]}
                        >
                          <button
                            type="button"
                            className="ideas-sidebar__menu-trigger"
                            onMouseDown={(event) => event.stopPropagation()}
                            aria-label="Ideas actions"
                            title="Actions"
                          >
                            <i className="bi bi-three-dots-vertical"></i>
                          </button>
                        </PortalDropdown>
                      </div>
                    </div>
                  </article>
                );
              })}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
